// =============================================================================
// features/assistant/progressLabels.ts — Nome de ferramenta → texto na tela.
//
// O backend manda só o NOME da ferramenta no evento de progresso; o texto
// legível é montado aqui. Duas razões:
//
//   1. LGPD (doc 17): os argumentos das tools carregam lead_id, telefone e nome.
//      Mandando só o nome, o payload que trafega pelo socket não tem PII —
//      e não há como vazar por descuido de quem escrever a próxima tool.
//   2. Mudar a redação é deploy de front, não do serviço Python.
//
// Ferramenta desconhecida cai num rótulo genérico honesto em vez de mostrar o
// identificador técnico ao operador — uma tool nova nunca deve vazar
// `call_whatever_v2` na tela.
// =============================================================================

/** Rótulos por ferramenta do copiloto. Chave = nome exato vindo do agente. */
const TOOL_LABELS: Readonly<Record<string, string>> = {
  find_lead: 'Procurando o cliente',
  get_lead_count: 'Contando leads',
  get_funnel_metrics: 'Lendo as métricas do funil',
  list_stage_leads: 'Listando leads do estágio',
  summarize_lead_conversation: 'Lendo a conversa do cliente',
  get_analysis_status: 'Verificando a análise de crédito',
  get_billing_snapshot: 'Consultando cobranças',
};

/** Texto exibido quando o agente está pensando, sem ferramenta em curso. */
export const THINKING_LABEL = 'Analisando sua pergunta';

/** Texto de uma ferramenta desconhecida (tool nova ainda sem rótulo). */
const FALLBACK_LABEL = 'Consultando seus dados';

/** Rótulo de UMA ferramenta. */
export function toolLabel(tool: string): string {
  return TOOL_LABELS[tool] ?? FALLBACK_LABEL;
}

/**
 * Monta a frase de um round de progresso.
 *
 * O agente costuma disparar várias ferramentas no mesmo round (ex.: resumir três
 * conversas). Repetir o mesmo rótulo três vezes é ruído, então deduplicamos e
 * indicamos a quantidade — "Lendo a conversa do cliente (3)" diz mais do que a
 * mesma linha repetida.
 */
export function progressLabel(tools: string[]): string {
  if (tools.length === 0) return THINKING_LABEL;

  const counts = new Map<string, number>();
  for (const tool of tools) {
    const label = toolLabel(tool);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([label, count]) => (count > 1 ? `${label} (${String(count)})` : label))
    .join(' · ');
}
